/**
 * Scroll & list-state memory for back-navigation.
 *
 * When a shopper scrolls halfway down a collection, taps a product, and
 * then hits back — we need to restore BOTH:
 *   1. their scroll position on the collection page, and
 *   2. the loaded product list (page N of infinite scroll), so the y-offset
 *      actually maps to a rendered card and not to a still-empty area.
 *
 * Two-tier storage:
 *   - In-memory `Map`: instant restore during SPA nav on the same tab.
 *   - `sessionStorage`: survives full page reloads (mobile Safari on
 *     PWA installs and Android Chrome's aggressive back-nav often
 *     force a reload; the in-memory Map is wiped by definition on
 *     reload — sessionStorage isn't). Persists the FULL snapshot,
 *     not just the scroll offset, so a heavy paginator (~10 pages =
 *     240 products loaded, y ≈ 15000 px) still rehydrates the whole
 *     grid instantly on back-nav.
 *
 * Per-tab quota is ~5 MB. 240 products ≈ 120 KB. Comfortable.
 */
const store = new Map();

const SS_PREFIX = "lmr:cat_state:";
const SS_MAX_AGE_MS = 15 * 60 * 1000; // 15 min — beyond this a shopper is probably starting over
const MAX_PERSISTED_PRODUCTS = 300; // hard cap so we never blow the 5MB quota

function _ssKey(k) {
  return SS_PREFIX + k;
}

function _ssRead(key) {
  try {
    const raw = sessionStorage.getItem(_ssKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.scrollY !== "number") return null;
    if (!parsed.at || Date.now() - parsed.at > SS_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function _ssWrite(key, snapshot) {
  try {
    // Cap the product list so a very deep paginator can't blow the
    // 5 MB per-origin quota. If they'd loaded more, we'll still
    // restore what we have and their scrollY might land close.
    const products = Array.isArray(snapshot.products)
      ? snapshot.products.slice(0, MAX_PERSISTED_PRODUCTS)
      : [];
    const payload = {
      scrollY: snapshot.scrollY ?? 0,
      products,
      total: snapshot.total ?? 0,
      page: snapshot.page ?? 1,
      hasMore: snapshot.hasMore ?? true,
      category: snapshot.category ?? null,
      at: Date.now(),
    };
    sessionStorage.setItem(_ssKey(key), JSON.stringify(payload));
  } catch {
    // Quota exceeded or SSR: best-effort. Fall back to a smaller
    // scroll-only payload so at least the offset is preserved.
    try {
      sessionStorage.setItem(
        _ssKey(key),
        JSON.stringify({ scrollY: snapshot.scrollY ?? 0, at: Date.now() }),
      );
    } catch {
      /* still can't — give up */
    }
  }
}

export function saveListState(key, data) {
  if (!key || !data) return;
  // Guard against transient `scrollY=0` saves. When the shopper taps a
  // product tile, `ProductPage`'s mount hook fires `window.scrollTo(0,0)`
  // to open the PDP at the top; that scroll event bubbles to any
  // still-attached listeners on the previous page (React 18 fires
  // setups from the new tree BEFORE cleanups from the old tree finish
  // in some interleavings). Result: `lastScrollYRef.current` is stomped
  // to 0 right before the persist-cleanup saves state. If we accept
  // that save we clobber the shopper's real 8000-px offset with 0 and
  // the NEXT back-nav restores to top.
  //
  // Fix: reject a `scrollY === 0` save when EITHER the in-memory Map or
  // the sessionStorage shadow already carries a meaningful non-zero
  // offset. Products / page / hasMore fields still advance (those
  // legitimately grow with infinite scroll); only the scroll offset is
  // preserved. Shoppers rarely deliberately park at y=0 so preserving
  // the older non-zero value is virtually always correct.
  const existing = store.get(key);
  const existingSs = _ssRead(key);
  const bestExistingY = Math.max(
    Number.isFinite(existing?.scrollY) ? existing.scrollY : 0,
    Number.isFinite(existingSs?.scrollY) ? existingSs.scrollY : 0,
  );
  const incomingY = Number.isFinite(data.scrollY) ? data.scrollY : 0;
  if (incomingY === 0 && bestExistingY > 100) {
    const merged = { ...data, scrollY: bestExistingY, savedAt: Date.now() };
    store.set(key, merged);
    _ssWrite(key, merged);
    return;
  }
  store.set(key, { ...data, savedAt: Date.now() });
  _ssWrite(key, data);
}

export function loadListState(key) {
  if (!key) return null;
  const inMem = store.get(key);
  if (inMem) return inMem;
  const persisted = _ssRead(key);
  if (persisted) {
    // Warm the in-memory Map so subsequent lookups on this tab are
    // free — and downstream consumers see the same shape either way.
    store.set(key, { ...persisted, savedAt: persisted.at });
    return persisted;
  }
  return null;
}

export function saveScrollOnly(key, scrollY) {
  if (!key || typeof scrollY !== "number") return;
  const existing = store.get(key);
  if (existing) {
    const next = { ...existing, scrollY, savedAt: Date.now() };
    store.set(key, next);
    _ssWrite(key, next);
    return;
  }
  // No existing snapshot — write just the scroll offset.
  try {
    sessionStorage.setItem(
      _ssKey(key),
      JSON.stringify({ scrollY, at: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Last-clicked product anchor.
 *
 * When a shopper taps a product tile we stash the slug + the listing
 * URL that referred it. On back-nav the listing page consumes this
 * (one-shot) and centres that specific tile in the viewport instead
 * of restoring just the raw scroll offset. Feels like the browser
 * "remembered" exactly where the shopper left off, tile-precise.
 */
const LC_KEY = "lmr:last_clicked";

export function saveLastClickedProduct(slug, memoryKey) {
  if (!slug || !memoryKey) return;
  try {
    sessionStorage.setItem(
      LC_KEY,
      JSON.stringify({ slug, memoryKey, at: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}

export function consumeLastClickedProduct(memoryKey) {
  try {
    const raw = sessionStorage.getItem(LC_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.slug || parsed.memoryKey !== memoryKey) return null;
    if (!parsed.at || Date.now() - parsed.at > SS_MAX_AGE_MS) return null;
    // One-shot: subsequent renders / re-mounts don't yank the shopper
    // back to the same anchor if they scroll away.
    sessionStorage.removeItem(LC_KEY);
    return parsed.slug;
  } catch {
    return null;
  }
}

export function peekLastClickedProduct(memoryKey) {
  try {
    const raw = sessionStorage.getItem(LC_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.slug || parsed.memoryKey !== memoryKey) return null;
    if (!parsed.at || Date.now() - parsed.at > SS_MAX_AGE_MS) return null;
    return parsed.slug;
  } catch {
    return null;
  }
}

export function clearListState(key) {
  if (!key) return;
  store.delete(key);
  try {
    sessionStorage.removeItem(_ssKey(key));
  } catch {
    /* ignore */
  }
}
